import { useContext, useState } from "react";
import { Context } from "../../../context/Context";
import { Archive, Button, Eye, Loader, RefreshCcw, Sparkles, Tags } from "../../ui";
import { ConfirmModal } from "../../modals/Confirm";
import { archiveAiImportAPI, retryAiImportAPI } from "../../../utils/api/menuAiImport";
import { aiImportText } from "./aiImportText";
import { menuErrorMessage } from "../menuRole";
import "./AiImport.css";
import { ACTIVE_STATUSES, STATUS_VARIANT, aiImportErrorMessage } from "./aiImportModel";
import { aiWalletDraftFailure, aiWalletErrorText } from "../../billing/aiWallet/AiBalanceNotice";
import { aiWalletMenuText, aiWalletRechargePath } from "../../billing/aiWallet/aiWalletMenu";

// Why a draft failed, in the page language; for a run the AI Wallet refused,
// the balance, required amount and shortage when this user may see them.
const failureText = (draft, lang) => (draft.walletError ? aiWalletErrorText({ code: draft.walletError.code, details: { wallet: draft.walletError.wallet } }, lang) : "") || aiWalletDraftFailure(draft.processing.error.code, lang) || draft.processing.error.message;

// Drafts of the open restaurant, above the menus. Drafts live on the server,
// so they survive reloads; the page polls while any of them is processing.
export default function AiImportDraftsPanel({ authorize, drafts, canWrite, onOpen, onChanged, onOpenMenu, onError }) {
  const { lang } = useContext(Context);
  const t = aiImportText(lang);
  const [busyId, setBusyId] = useState("");
  const [confirmArchive, setConfirmArchive] = useState(null);
  const visible = drafts.filter((d) => d.status !== "archived");
  if (!visible.length) return null;

  const run = async (id, action) => {
    // Retry / archive act on drafts of the open restaurant: check it at the moment of the call.
    if (!authorize?.()) return onError(menuErrorMessage({ status: 403 }, lang));
    setBusyId(id);
    try {
      await action();
      await onChanged();
    } catch (err) {
      onError(aiImportErrorMessage(err, t, lang));
    } finally {
      setBusyId("");
    }
  };

  return (
    <section className="ai-import-drafts" aria-label={t.draftsTitle}>
      <h3 className="ai-import-drafts-title">
        <Sparkles size={16} aria-hidden="true" /> {t.draftsTitle}
      </h3>
      <ul>
        {visible.map((draft) => {
          const active = ACTIVE_STATUSES.includes(draft.status);
          const stage = draft.status === "processing" ? t.stages[draft.processing?.stage] : "";
          return (
            <li key={draft._id} className={`ai-import-draft-row is-${draft.status}`}>
              <div className="ai-import-draft-main">
                <strong className="ai-import-draft-name">{draft.source.fileName}</strong>
                <span className="menu-secondary">
                  {new Date(draft.createdAt).toLocaleString(lang === "ar" ? "ar-SA" : "en-GB", { dateStyle: "medium", timeStyle: "short" })}
                  {draft.uploadedBy?.name ? ` · ${t.uploadedBy(draft.uploadedBy.name)}` : ""}
                </span>
                {draft.status === "failed" && draft.processing?.error?.message && <span className="menu-secondary is-warn">{failureText(draft, lang)}</span>}
                {draft.status === "failed" && draft.walletError?.wallet?.canAddFunds && draft.walletError.wallet.shortageMinor > 0 && (
                  <a className="aiw-inline-link" href={aiWalletRechargePath(draft.walletError.wallet.storeId)}>
                    {aiWalletMenuText(lang).recharge}
                  </a>
                )}
                {draft.status === "needs_review" && draft.summary && <span className="menu-secondary">{t.summaryDetected(draft.summary.detected)}</span>}
              </div>
              <Tags variant={STATUS_VARIANT[draft.status]} size="sm" icon={active ? <Loader size={13} className="ai-import-spin" /> : null}>
                {stage ? `${t.status[draft.status]} · ${stage}` : t.status[draft.status]}
              </Tags>
              <span className="ai-import-draft-actions">
                {["needs_review", "failed", "approving", "processing", "uploaded"].includes(draft.status) && (
                  <Button type="button" variant="primary" appearance={draft.status === "needs_review" ? "filled" : "soft"} size="xs" rounded icon={<Eye size={14} />} onClick={() => onOpen(draft._id)}>
                    {t.review}
                  </Button>
                )}
                {draft.status === "approved" && draft.approval?.menuId && (
                  <Button type="button" variant="success" appearance="soft" size="xs" rounded onClick={() => onOpenMenu(draft.approval.menuId)}>
                    {t.openMenu}
                  </Button>
                )}
                {canWrite && draft.status === "failed" && draft.processing?.error?.retryable !== false && (
                  <Button type="button" variant="primary" appearance="soft" size="xs" rounded icon={<RefreshCcw size={14} />} loading={busyId === draft._id} onClick={() => run(draft._id, () => retryAiImportAPI(draft._id))}>
                    {t.retry}
                  </Button>
                )}
                {canWrite && ["needs_review", "failed"].includes(draft.status) && (
                  <Button
                    type="button"
                    variant="warning"
                    appearance="soft"
                    size="xs"
                    rounded
                    icon={<Archive size={14} />}
                    aria-label={`${t.archive} ${draft.source.fileName}`}
                    disabled={busyId === draft._id}
                    onClick={() => setConfirmArchive(draft)}
                  />
                )}
              </span>
            </li>
          );
        })}
      </ul>
      {confirmArchive && (
        <ConfirmModal
          title={t.archive}
          message={`${confirmArchive.source.fileName}
${t.archiveConfirm}`}
          confirmText={t.archive}
          cancelText={t.cancel}
          onCancel={() => setConfirmArchive(null)}
          onConfirm={async () => {
            const id = confirmArchive._id;
            setConfirmArchive(null);
            await run(id, () => archiveAiImportAPI(id));
          }}
        />
      )}
    </section>
  );
}
